import { Search as SearchIcon } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    formatDateString,
    IOrganisation,
    mapDateStringToApiDate,
    Page,
    PermissionKeys,
    RowActions,
    SortOrder,
    Table,
    useHasPermission,
    useQueryParamsContext,
} from '../../../shared';
import { ShipmentsFilter } from '../../components';
import { useDeleteShipment, useShipmentsList } from '../../hooks';

export const ShipmentsPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.SHIPMENTS_WRITE);

    const {
        queryParams: { page, pageSize, filter, sort },
        setFilter,
        setPage,
        setPageSize,
        setSort,
    } = useQueryParamsContext('shipments', {
        sort: [{ field: 'shipmentDate', sort: 'desc' }],
    });

    const { mutateAsync: deleteShipment } = useDeleteShipment();
    const { isPending, data } = useShipmentsList({
        page: page,
        pageSize: pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        shipmentDate: mapDateStringToApiDate(filter.shipmentDate) || undefined,
        status: filter.status || undefined,
        receiverId: filter.receiverId || undefined,
        mailStatus: filter.mailStatus || undefined,
    });

    const columns: GridColDef[] = [
        {
            field: 'shipmentDate',
            valueFormatter: (value: string) => formatDateString(value),
            headerName: t('shipmentDate'),
            width: 150,
        },
        {
            field: 'receiver',
            headerName: t('receiver'),
            valueFormatter: (value: IOrganisation) => value?.name,
            minWidth: 150,
            flex: 1,
            sortable: false,
        },
        {
            field: 'sequenceNumber',
            headerName: t('sequenceNumber'),
            width: 68,
            sortable: false,
        },
        {
            field: 'status',
            headerName: t('status'),
            valueFormatter: (value: string) => t(value),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'mailStatus',
            headerName: t('mailStatus'),
            valueFormatter: (value: string) => t(value),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 140,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id } }: GridCellParams) => (
                <>
                    <IconButton onClick={() => navigate(`/planning/shipments/${id}/detail`)}>
                        <SearchIcon />
                    </IconButton>
                    {hasWritePermission && (
                        <RowActions
                            onEdit={() => navigate(`/planning/shipments/${id}`)}
                            onDelete={() => deleteShipment(id)}
                            deleteWarningTitle={t('removeShipment')}
                            deleteWarningText={t('removeShipmentWarning')}
                        />
                    )}
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('shipments')}
            filter={<ShipmentsFilter filter={filter} onChange={setFilter} />}
            actions={
                hasWritePermission
                    ? [
                          <Button
                              component={Link}
                              to="/planning/shipments/shipments-complete-wizard"
                              color="primary"
                              variant="outlined"
                          >
                              {t('completeShipments')}
                          </Button>,
                          <Button
                              component={Link}
                              to="/planning/shipments/new"
                              color="primary"
                              variant="contained"
                              data-testid="new-shipment"
                          >
                              {t('newShipment')}
                          </Button>,
                      ]
                    : []
            }
        >
            <Table
                rows={data?.data || []}
                columns={columns}
                rowCount={data?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                onRowClick={(row) => navigate(`/planning/shipments/${row.id}/detail`)}
            />
        </Page>
    );
};
